// =============================================================================
// ANCHOR VALIDATION HELPERS - Strict On-Chain Enforcement
// =============================================================================
import { Connection, PublicKey, AccountInfo } from "@solana/web3.js";
import { getMint, getAccount, getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { PROGRAM_IDS, findTokenConfigPDA, findPoolPDA } from "./idl-types";

/**
 * Validates that a Solana program is deployed and executable on-chain
 */
export async function validateProgramDeployment(
  connection: Connection,
  programId: string,
  programName: string
): Promise<void> {
  if (!programId) {
    throw new Error(`${programName} program ID not configured - strict on-chain enforcement required`);
  }

  try {
    const pubkey = new PublicKey(programId);
    const accountInfo = await connection.getAccountInfo(pubkey);

    if (!accountInfo) {
      throw new Error(`${programName} program not deployed on-chain at ${programId}`);
    }

    if (!accountInfo.executable) {
      throw new Error(`${programName} program at ${programId} is not executable`);
    }

    console.log(`${programName} program validated: ${programId}`);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to validate ${programName} program: ${String(error)}`);
  }
}

/**
 * Validates all required Moonrun programs are deployed
 */
export async function validateAllPrograms(connection: Connection): Promise<void> {
  if (!PROGRAM_IDS.TOKEN || !PROGRAM_IDS.BONDING_CURVE || !PROGRAM_IDS.GOVERNANCE) {
    throw new Error("All program IDs must be configured - strict on-chain enforcement required");
  }

  await validateProgramDeployment(connection, PROGRAM_IDS.TOKEN, "Token");
  await validateProgramDeployment(connection, PROGRAM_IDS.BONDING_CURVE, "Bonding Curve");
  await validateProgramDeployment(connection, PROGRAM_IDS.GOVERNANCE, "Governance");
}

/**
 * Validates that a token exists on-chain and has proper program accounts
 */
export async function validateTokenOnChain(
  connection: Connection,
  mintAddress: string
): Promise<{
  mintInfo: any;
  tokenConfigPDA: PublicKey;
  poolPDA: PublicKey;
}> {
  const mintPubkey = new PublicKey(mintAddress);

  // Validate mint exists
  const mintInfo = await getMint(connection, mintPubkey);

  // Derive and validate PDAs
  const [tokenConfigPDA] = findTokenConfigPDA(mintPubkey, new PublicKey(PROGRAM_IDS.TOKEN!));
  const [poolPDA] = findPoolPDA(mintPubkey, new PublicKey(PROGRAM_IDS.BONDING_CURVE!));

  // Check token config account exists
  const tokenConfigAccount = await connection.getAccountInfo(tokenConfigPDA);
  if (!tokenConfigAccount) {
    throw new Error(`Token config PDA not found - token may not be properly created: ${tokenConfigPDA.toBase58()}`);
  }

  // Check pool account exists
  const poolAccount = await connection.getAccountInfo(poolPDA);
  if (!poolAccount) {
    throw new Error(`Pool PDA not found - bonding curve may not be initialized: ${poolPDA.toBase58()}`);
  }

  return {
    mintInfo,
    tokenConfigPDA,
    poolPDA,
  };
}

/**
 * Validates a Solana address format (base58 encoded public key)
 */
export function validateSolanaAddress(address: string, fieldName: string = "address"): void {
  try {
    new PublicKey(address);
    if (address.length < 32 || address.length > 44) {
      throw new Error("Invalid length");
    }
  } catch (error) {
    throw new Error(`Invalid ${fieldName} format: ${address}`);
  }
}

/**
 * Validates a transaction signature format
 */
export function validateTransactionSignature(signature: string): void {
  if (!signature || signature.length < 64 || signature.length > 88) {
    throw new Error(`Invalid transaction signature format: ${signature}`);
  }

  // Basic base58 validation
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
  if (!base58Regex.test(signature)) {
    throw new Error(`Invalid transaction signature characters: ${signature}`);
  }
}

/**
 * Comprehensive token validation including on-chain state
 */
export async function validateCompleteTokenDeployment(
  connection: Connection,
  mintAddress: string,
  expectedTokenConfigPDA?: string,
  expectedPoolPDA?: string
): Promise<{
  isValid: boolean;
  mintInfo: any;
  tokenConfigPDA: PublicKey;
  poolPDA: PublicKey;
  errors: string[];
}> {
  const errors: string[] = [];
  let mintInfo: any = null;
  let tokenConfigPDA: PublicKey | null = null;
  let poolPDA: PublicKey | null = null;

  try {
    // Validate programs first
    await validateAllPrograms(connection);
  } catch (error) {
    errors.push(`Program validation failed: ${error instanceof Error ? error.message : String(error)}`);
    return { isValid: false, mintInfo: null, tokenConfigPDA: null as any, poolPDA: null as any, errors };
  }

  try {
    validateSolanaAddress(mintAddress, "mint address");
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }

  if (errors.length === 0) {
    try {
      const result = await validateTokenOnChain(connection, mintAddress);
      mintInfo = result.mintInfo;
      tokenConfigPDA = result.tokenConfigPDA;
      poolPDA = result.poolPDA;

      // Validate expected PDAs if provided
      if (expectedTokenConfigPDA && tokenConfigPDA.toBase58() !== expectedTokenConfigPDA) {
        errors.push(`Token config PDA mismatch. Expected: ${expectedTokenConfigPDA}, Actual: ${tokenConfigPDA.toBase58()}`);
      }

      if (expectedPoolPDA && poolPDA.toBase58() !== expectedPoolPDA) {
        errors.push(`Pool PDA mismatch. Expected: ${expectedPoolPDA}, Actual: ${poolPDA.toBase58()}`);
      }

    } catch (error) {
      errors.push(`On-chain validation failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  return {
    isValid: errors.length === 0,
    mintInfo,
    tokenConfigPDA: tokenConfigPDA!,
    poolPDA: poolPDA!,
    errors,
  };
}

/**
 * Validates user token balance on-chain
 */
export async function validateUserTokenBalance(
  connection: Connection,
  mintAddress: string,
  userWallet: string
): Promise<bigint> {
  const mintPubkey = new PublicKey(mintAddress);
  const userPubkey = new PublicKey(userWallet);

  const userAta = await getAssociatedTokenAddress(mintPubkey, userPubkey);

  try {
    const accountInfo = await getAccount(connection, userAta);
    return accountInfo.amount;
  } catch (error) {
    // User doesn't have token account yet
    return 0n;
  }
}